import Grid from '@mui/material/Grid';
import { GridColumn } from '@progress/kendo-react-grid';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ActionButton,
  DeleteButton
} from 'src/components/shared/Button/Button';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { GradeDetailDto, GradeParameterDto } from 'src/store/grades/grade';
import { ParameterDto } from 'src/store/parameters/parameter';
import { selectActiveParameters } from 'src/store/parameters/parameters.selectors';

interface Props {
  grade: Nullable<GradeDetailDto>;
  onParametersChanged: (parameters: GradeParameterDto[]) => void;
}

export const GradeParameters: React.FC<Props> = ({
  grade,
  onParametersChanged
}) => {
  const { t } = useTranslation();

  const [gradeParameters, setGradeParameters] = useState<GradeParameterDto[]>(
    []
  );
  const [parameters, setParameters] = useState<ParameterDto[]>([]);

  const parameters$ = useSelector(selectActiveParameters);

  useEffect(() => {
    if (grade?.gradeParameters) {
      setGradeParameters(grade.gradeParameters);
    }
  }, [grade]);

  useEffect(() => {
    setParameters(
      parameters$.filter(
        parameter =>
          !gradeParameters.some(gp => gp.parameterId === parameter.id)
      )
    );
  }, [gradeParameters, parameters$]);

  const onAddParameter = (parameter: ParameterDto) => {
    const parameters = [
      {
        gradeId: grade?.id,
        parameterId: parameter.id,
        parameterName: parameter.name
      } as GradeParameterDto,
      ...gradeParameters
    ];
    setGradeParameters(parameters);
    onParametersChanged(parameters);
  };

  const onDeleteParameter = (parameter: GradeParameterDto) => {
    const parameters = gradeParameters.filter(
      gp => gp.parameterId !== parameter.parameterId
    );
    setGradeParameters(parameters);
    onParametersChanged(parameters);
  };

  return (
    <Grid container>
      <Grid item md={6}>
        <DataGrid
          items={gradeParameters}
          style={{ height: 400 }}
          initialSort={[{ field: 'parameterName', dir: 'asc' }]}
        >
          <GridColumn
            field="parameterName"
            title={t('parameter.plural')}
            cell={props => (
              <td>
                <div className="flex jc-sb align-center">
                  <BodyType truncate style={{ maxWidth: '80%' }}>
                    {props.dataItem.parameterName}
                  </BodyType>
                  <DeleteButton
                    onClick={() => onDeleteParameter(props.dataItem)}
                  />
                </div>
              </td>
            )}
          />
        </DataGrid>
      </Grid>
      <Grid item md={6}>
        <DataGrid
          items={parameters}
          style={{ height: 400 }}
          initialSort={[{ field: 'name', dir: 'asc' }]}
        >
          <GridColumn
            field="name"
            title={t('parameter.all')}
            cell={props => (
              <td>
                <div className="flex jc-sb align-center">
                  <BodyType truncate style={{ maxWidth: '80%' }}>
                    {props.dataItem.name}
                  </BodyType>
                  <ActionButton
                    icon="la la-plus"
                    onClick={() => onAddParameter(props.dataItem)}
                  />
                </div>
              </td>
            )}
          />
        </DataGrid>
      </Grid>
    </Grid>
  );
};
